import { useId, type KeyboardEvent } from 'react'

/**
 * Balken- oder Liniendiagramm als SVG, ohne Bibliothek. Der Assistent nutzt es für seine ```ui-Blöcke
 * (UiBlock.tsx), der Verlauf der Rohstoffnotierungen für die Preise je Lieferant.
 *
 * Farbe trägt nie allein: jede Reihe hat zusätzlich ein Muster (Balken) bzw. einen Strich und eine
 * Markenform (Linie), und die Legende nennt den Namen. Zahlen deutsch.
 *
 * Mit `onReihe` sind Legende, Linie und Marken klickbar (Verlauf: „In der Tabelle zeigen“).
 */

export interface DiagrammReihe {
  name: string
  werte: (number | null)[]
  /** Zusatz je Punkt für den Tooltip, etwa „Mittel aus 2 von 3 Monaten“. */
  hinweise?: (string | null)[]
}

export interface DiagrammEigenschaften {
  typ: 'balken' | 'linie'
  titel?: string
  x: string[]
  reihen: DiagrammReihe[]
  einheit?: string
  /**
   * Die y-Achse beginnt bei null (Mengen, Balken). Für Preise um 1.450 €/t wäre jede Linie flach -
   * dort `false`: die Achse umfasst nur die Spanne der Werte.
   */
  abNull?: boolean
  /** Werte an jede Marke schreiben; bei vielen Punkten unlesbar, dann steht der Wert im Tooltip. */
  werteZeigen?: boolean
  /** Volle Breite und höher - für eine Seite, deren Hauptsache das Diagramm ist (Verlauf der Notierungen). */
  gross?: boolean
  /**
   * Klick auf eine Reihe: Legende und Linie melden nur die Reihe, eine Marke bzw. ein Balken auch den Punkt
   * (Index in `x`). Ohne Rückruf ist nichts klickbar (Chat).
   */
  onReihe?: (reihe: number, punkt?: number) => void
  /** was der Klick tut - steht im Tooltip, etwa „In der Tabelle zeigen“ */
  klickText?: string
}

// Farbe je Reihe, dazu Muster (Balken) bzw. Strich und Marke (Linie), damit Farbe nie allein trägt.
// Acht gut unterscheidbare Farben, damit alle Lieferanten eines Rohstoffs eine eigene Linie haben (Rapsöl hat sechs).
const REIHEN_FARBEN = ['#009fe3', '#c2410c', '#15803d', '#7c3aed', '#1c2833', '#be185d', '#0f766e', '#a16207']
const REIHEN_STRICHE = ['', '6 3', '2 3', '8 3 2 3', '10 4', '4 2 1 2', '1 3', '12 3 3 3']
const HOECHSTENS_REIHEN = REIHEN_FARBEN.length

const zahlFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 })

/** Ein runder Abstand für das Gitter: 1, 2 oder 5 mal eine Zehnerpotenz, etwa vier Linien. */
function schoenerSchritt(spanne: number): number {
  if (spanne <= 0) return 1
  const roh = spanne / 4
  const zehner = 10 ** Math.floor(Math.log10(roh))
  const rest = roh / zehner
  const faktor = rest <= 1 ? 1 : rest <= 2 ? 2 : rest <= 5 ? 5 : 10
  return faktor * zehner
}

function marke(form: number, x: number, y: number, farbe: string, schluessel: string) {
  const r = 3.5
  switch (form % 6) {
    case 0:
      return <circle key={schluessel} cx={x} cy={y} r={r} fill={farbe} />
    case 1:
      return <rect key={schluessel} x={x - r} y={y - r} width={2 * r} height={2 * r} fill={farbe} />
    case 2:
      return <polygon key={schluessel} points={`${x},${y - r - 1} ${x + r + 1},${y} ${x},${y + r + 1} ${x - r - 1},${y}`} fill={farbe} />
    case 3:
      return <polygon key={schluessel} points={`${x},${y - r - 1} ${x + r + 1},${y + r} ${x - r - 1},${y + r}`} fill={farbe} />
    case 4:
      return <circle key={schluessel} cx={x} cy={y} r={r} fill="#ffffff" stroke={farbe} strokeWidth={2} />
    default:
      return <rect key={schluessel} x={x - r} y={y - r} width={2 * r} height={2 * r} fill="#ffffff" stroke={farbe} strokeWidth={2} />
  }
}

/** Füllung eines Balkens: Vollton, Schraffur, Punkte, weiß mit Rand - je Reihe eine andere. */
function fuellung(r: number, id: string): string {
  if (r === 1) return `url(#${id}-schraffur)`
  if (r === 2) return `url(#${id}-punkte)`
  if (r >= 3) return '#ffffff'
  return REIHEN_FARBEN[0]
}

export default function Diagramm({
  typ,
  titel,
  x,
  reihen: alleReihen,
  einheit,
  abNull = true,
  werteZeigen = true,
  gross = false,
  onReihe,
  klickText = 'anklicken',
}: DiagrammEigenschaften) {
  const id = useId().replace(/:/g, '')
  // Klickbares bekommt Zeiger, Tooltip-Zusatz und - in der Legende - die Tastatur (Enter, Leertaste).
  const klickZusatz = onReihe ? ` - ${klickText}` : ''
  const klick = (r: number, punkt?: number) =>
    onReihe ? { className: 'ui-klickbar', onClick: () => onReihe(r, punkt) } : {}
  const tastatur = (r: number) =>
    onReihe
      ? {
          role: 'button',
          tabIndex: 0,
          'aria-label': `${alleReihen[r]?.name}: ${klickText}`,
          onKeyDown: (e: KeyboardEvent<SVGGElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onReihe(r)
            }
          },
        }
      : {}
  const reihen = alleReihen.slice(0, HOECHSTENS_REIHEN)
  const werte = reihen.flatMap((r) => r.werte.filter((w): w is number => w !== null))
  const balken = typ === 'balken'
  // Balken brauchen die Null als Grundlinie, sonst lügt ihre Höhe.
  const mitNull = abNull || balken
  const roheMax = werte.length ? Math.max(...werte) : 0
  const roheMin = werte.length ? Math.min(...werte) : 0
  const maxWert = mitNull ? Math.max(0, roheMax) : roheMax
  const minWert = mitNull ? Math.min(0, roheMin) : roheMin
  const schritt = schoenerSchritt(maxWert - minWert || Math.abs(maxWert) * 0.02 || 1)
  let oben = Math.ceil(maxWert / schritt) * schritt
  let unten = Math.floor(minWert / schritt) * schritt
  if (oben === unten) {
    oben += schritt
    if (!mitNull) unten -= schritt
  }
  const ticks: number[] = []
  for (let t = unten; t <= oben + 1e-9; t += schritt) ticks.push(Math.round(t / schritt) * schritt)

  // Viele Punkte (ein Verlauf über Wochen) rücken zusammen; beschriftet wird dann nur jeder n-te Tag.
  const viele = !balken && x.length > 16
  const links = 64
  const rechts = 16
  const obenRand = 22
  const xHoehe = !viele && x.some((b) => b.length > 6) ? 48 : 22
  const plotHoehe = gross ? 320 : 180
  const legendeZeilen = Math.ceil(reihen.length / 3)
  const legendeHoehe = 18 * legendeZeilen + 4
  const gruppenBreite = balken ? Math.max(40, reihen.length * 18 + 16) : viele ? Math.max(12, 860 / x.length) : 56
  const plotBreite = Math.max(gross ? 1040 : viele ? 560 : 300, x.length * gruppenBreite)
  const breite = links + plotBreite + rechts
  const hoehe = obenRand + plotHoehe + xHoehe + legendeHoehe
  const xSchritt = viele ? Math.ceil(x.length / 12) : 1
  const beschriften = werteZeigen && !viele

  const y = (w: number) => obenRand + plotHoehe - ((w - unten) / (oben - unten)) * plotHoehe
  const xMitte = (i: number) => links + (i + 0.5) * (plotBreite / x.length)
  const einheitText = einheit ? ` ${einheit}` : ''
  const wertText = (w: number) => zahlFormat.format(w)
  const grundlinie = mitNull ? y(0) : y(unten)

  return (
    <div className="ui-block ui-diagramm">
      {titel && <h4>{titel}</h4>}
      <div className="ui-diagramm-rahmen">
        <svg viewBox={`0 0 ${breite} ${hoehe}`} style={{ width: '100%', maxWidth: gross ? undefined : breite, minWidth: viele || gross ? 560 : undefined, height: 'auto' }} role="img" aria-label={titel ?? 'Diagramm'}>
          <defs>
            <pattern id={`${id}-schraffur`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="#ffffff" />
              <rect width="3" height="6" fill={REIHEN_FARBEN[1]} />
            </pattern>
            <pattern id={`${id}-punkte`} width="5" height="5" patternUnits="userSpaceOnUse">
              <rect width="5" height="5" fill="#ffffff" />
              <circle cx="2.5" cy="2.5" r="1.4" fill={REIHEN_FARBEN[2]} />
            </pattern>
          </defs>
          {/* Gitter und Y-Achse */}
          {ticks.map((t) => (
            <g key={t}>
              <line x1={links} x2={links + plotBreite} y1={y(t)} y2={y(t)} stroke="#dde3e8" strokeWidth={1} />
              <text x={links - 6} y={y(t) + 3.5} fontSize={10} textAnchor="end" fill="#6b7785">
                {wertText(t)}
              </text>
            </g>
          ))}
          <line x1={links} x2={links} y1={obenRand} y2={obenRand + plotHoehe} stroke="#6b7785" strokeWidth={1} />
          {einheit && (
            <text x={links - 6} y={obenRand - 8} fontSize={10} textAnchor="end" fill="#6b7785">
              {einheit}
            </text>
          )}
          {/* X-Beschriftung */}
          {x.map((b, i) =>
            i % xSchritt !== 0 && i !== x.length - 1 ? null : (
              <text
                key={i}
                x={xMitte(i)}
                y={obenRand + plotHoehe + 14}
                fontSize={10}
                fill="#1c2833"
                textAnchor={xHoehe > 22 ? 'end' : 'middle'}
                transform={xHoehe > 22 ? `rotate(-35 ${xMitte(i)} ${obenRand + plotHoehe + 14})` : undefined}
              >
                {b}
              </text>
            ),
          )}
          {/* Werte */}
          {balken
            ? reihen.map((reihe, r) => {
                const b = (plotBreite / x.length - 16) / reihen.length
                return x.map((xWert, i) => {
                  const w = reihe.werte[i]
                  if (w === null || w === undefined) return null
                  const x0 = xMitte(i) - (reihen.length * b) / 2 + r * b
                  const y0 = Math.min(y(w), grundlinie)
                  const h = Math.abs(y(w) - grundlinie)
                  return (
                    <g key={`${r}-${i}`}>
                      <rect x={x0 + 1} y={y0} width={Math.max(b - 2, 2)} height={h} fill={fuellung(r, id)} stroke={REIHEN_FARBEN[r]} strokeWidth={1} {...klick(r, i)}>
                        <title>{`${reihe.name}, ${xWert}: ${wertText(w)}${einheitText}${klickZusatz}`}</title>
                      </rect>
                      {beschriften && (
                        <text x={x0 + b / 2} y={w >= 0 ? y0 - 3 : y0 + h + 10} fontSize={9} textAnchor="middle" fill="#1c2833">
                          {wertText(w)}
                        </text>
                      )}
                    </g>
                  )
                })
              })
            : reihen.map((reihe, r) => {
                const punkte = x.map((_, i) => ({ i, w: reihe.werte[i] })).filter((p): p is { i: number; w: number } => typeof p.w === 'number')
                const pfad = punkte.map((p, n) => `${n === 0 ? 'M' : 'L'}${xMitte(p.i)},${y(p.w)}`).join(' ')
                return (
                  <g key={r}>
                    <path d={pfad} fill="none" stroke={REIHEN_FARBEN[r]} strokeWidth={2} strokeDasharray={REIHEN_STRICHE[r] || undefined} />
                    {onReihe && (
                      // Die Linie ist 2 px dünn - ein unsichtbarer breiter Strich darüber nimmt den Klick an.
                      <path d={pfad} fill="none" stroke="transparent" strokeWidth={12} {...klick(r)}>
                        <title>{`${reihe.name}${klickZusatz}`}</title>
                      </path>
                    )}
                    {punkte.map((p) => (
                      <g key={p.i} {...klick(r, p.i)}>
                        {onReihe && <circle cx={xMitte(p.i)} cy={y(p.w)} r={8} fill="transparent" />}
                        {marke(r, xMitte(p.i), y(p.w), REIHEN_FARBEN[r], `m${p.i}`)}
                        <title>{`${reihe.name}, ${x[p.i]}: ${wertText(p.w)}${einheitText}${reihe.hinweise?.[p.i] ? ` (${reihe.hinweise[p.i]})` : ''}${klickZusatz}`}</title>
                        {beschriften && (
                          <text x={xMitte(p.i)} y={y(p.w) + (r % 2 === 0 ? -8 : 14)} fontSize={9} textAnchor="middle" fill="#1c2833">
                            {wertText(p.w)}
                          </text>
                        )}
                      </g>
                    ))}
                  </g>
                )
              })}
          {/* Legende: Muster bzw. Strich und Marke, dazu der Name - drei je Zeile */}
          {reihen.map((reihe, r) => {
            const lx = links + (r % 3) * (plotBreite / 3)
            const ly = obenRand + plotHoehe + xHoehe + 14 + Math.floor(r / 3) * 18
            return (
              <g key={r} {...klick(r)} {...tastatur(r)}>
                {onReihe && (
                  <>
                    <title>{`${reihe.name}${klickZusatz}`}</title>
                    <rect x={lx - 3} y={ly - 13} width={plotBreite / 3 - 8} height={18} fill="transparent" />
                  </>
                )}
                {balken ? (
                  <rect x={lx} y={ly - 9} width={12} height={10} fill={fuellung(r, id)} stroke={REIHEN_FARBEN[r]} />
                ) : (
                  <>
                    <line x1={lx} x2={lx + 18} y1={ly - 4} y2={ly - 4} stroke={REIHEN_FARBEN[r]} strokeWidth={2} strokeDasharray={REIHEN_STRICHE[r] || undefined} />
                    {marke(r, lx + 9, ly - 4, REIHEN_FARBEN[r], 'l')}
                  </>
                )}
                <text x={lx + (balken ? 16 : 22)} y={ly} fontSize={10} fill="#1c2833">
                  {reihe.name}
                </text>
              </g>
            )
          })}
        </svg>
      </div>
      {alleReihen.length > HOECHSTENS_REIHEN && (
        <p className="hinweis">
          Gezeigt sind die ersten {HOECHSTENS_REIHEN} von {alleReihen.length} Reihen.
        </p>
      )}
    </div>
  )
}
